import { useMemo } from "react"
import { dateFormat, distinct, flatJoin, flatten, numberFormat } from "shared/util"
import { useUserSession } from "features/session/context"
import { hiddenFlags } from 'features/search/util'

import { SearchDetailResult } from "interface/search"

import { GridColumnDefMap } from "components/grid/types"
import { formatter, columnBase, valueFormatter } from "components/grid/util"
import { renderer } from "components/grid/renderer"

import { JournalERAMap } from "../../types"
import { EvaluationFormResult } from "interface/evaluationForm"
import { KojinResultWithEvaluationSummary } from "interface/search/evaluation"

type useKojinGridColumnsProps = {
	search: SearchDetailResult
	evaluationForm: EvaluationFormResult | null
	onClickLink: (kojinCode: string) => void
}
export const useKojinGridColumns = ({
	search,
	evaluationForm,
	onClickLink
}: useKojinGridColumnsProps) => {
	const session = useUserSession()
	const {
		hideResult,
		hideDPC,
		hideCancer,
		hideTrial,
		hideResearch,
		hideJournal,
		hideConvention,
		hideGuideLine
	} = useMemo(
		() => hiddenFlags(search),
		[search]
	)
	const columnsDef = useMemo<GridColumnDefMap<KojinResultWithEvaluationSummary>>(
		() => {
			const columnsDef: GridColumnDefMap<KojinResultWithEvaluationSummary> = {
				'link': {
					headerName: '詳細',
					hideable: false,
					sticky: true,
					value: (r) => r.kojinCode,
					...columnBase.action.openByValue(onClickLink)
				},
				'rank': {
					headerName: '総合順位',
					hidden: hideResult,
					value: (r) => r.score?.rank,
					...columnBase.rank
				},
				'isiName': {
					headerName: '医師名',
					width: 150,
					hideable: false,
					sticky: true,
					value: (r) => [
						r.isiNameKana ?? '',
						r.isiName ?? ''
					],
					hasKana: true,
					filterType: 'text'
				},
				'seibetu': {
					headerName: '性別',
					width: 50,
					defaultShow: false,
					value: (r) => r.seibetuCode === '1' ? '男性' : r.seibetuCode === '2' ? '女性' : '不明',
					filterType: 'candidates'
				},
				'age': {
					headerName: '年齢',
					value: (r) => r.nenrei,
					...columnBase.age
				},
				'birthDate': {
					headerName: '生年月日',
					width: 196,
					defaultShow: false,
					value: (r) => r.seinenGappi,
					valueFormatter: (value) => dateFormat(value),
					filterType: 'date'
				},
				'organ': {
					headerName: '現所属病院名',
					width: 400,
					value: (r) => r.organ?.map(organ => flatJoin('\t',
						organ.sisetuName,
						organ.bukaName,
						organ.yakusyokuName
					)),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'text'
				},
				'block': {
					headerName: 'ブロック',
					width: 100,
					value: (r) => distinct(r.organ?.flatMap(organ => organ.sisetuBlockName ?? []) ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'todofuken': {
					headerName: '都道府県',
					width: 100,
					defaultShow: false,
					value: (r) => distinct(r.organ?.flatMap(organ => organ.sisetuKenName ?? []) ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'bedCount': {
					headerName: '最大病床数',
					defaultShow: false,
					value: (r) => r.organ?.reduce<number | null>(
						(acc, organ) => organ.bedCount ? Math.max(organ.bedCount, acc ?? 0) : acc, null
					) ?? null,
					...columnBase.bedCount
				},
				'sinryoka': {
					headerName: '診療科',
					width: 200,
					value: (r) => r.sinryoka?.flatMap(sinryoka => sinryoka.sinryokaName ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'senmoni': {
					headerName: '専門医',
					width: 200,
					defaultShow: false,
					value: (r) => r.senmoni?.flatMap(senmoni => senmoni.senmoniName ? (
						senmoni.sidoui ? `${senmoni.senmoniName}指導医` :
						senmoni.senmoni ? `${senmoni.senmoniName}専門医` :
						senmoni.ninteii ? `${senmoni.senmoniName}認定医` : senmoni.senmoniName
					) : []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'gakkai': {
					headerName: '所属学会',
					width: 200,
					defaultShow: false,
					value: (r) => r.gakkai?.flatMap(gakkai => gakkai.gakkaiName ?? []),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'sotukou': {
					headerName: '出身大学',
					width: 100,
					value: (r) => r.sotukouName,
					filterType: 'candidates'
				},
				'sotuNen': {
					headerName: '卒業年',
					align: 'right',
					defaultShow: false,
					width: 80,
					valueFormatter: formatter.year,
					value: (r) => r.sotugyouYear,
					filterType: 'histgram'
				},
				'primarySinryoka': {
					headerName: 'COM診療科',
					width: 100,
					defaultShow: false,
					hidden: session.isCareNet === false,
					value: (r) => r.primarySinryoka?.sinryokaName,
					filterType: 'candidates'
				},
				/*
				'comSinryoka': {
					headerName: 'COM診療科',
					width: 200,
					defaultShow: false,
					hidden: session.isCareNet === false,
					value: (r) => r.comSinryoka?.flatMap(sinryoka => sinryoka.sinryokaName ?? []),
					renderCell: (params) => renderer.multiRow(params.value),
					filterType: 'candidates'
				},
				*/
				'kanriBunrui': {
					headerName: '勤務先分類',
					width: 100,
					defaultShow: false,
					hidden: session.isCareNet === false,
					value: (r) => (
						r.kanriBunrui === 'D01' ? 'D01:診療所 経営者' :
						r.kanriBunrui === 'D02' ? 'D02:診療所 勤務医' :
						r.kanriBunrui === 'D03' ? 'D03:小規模病院' : 
						r.kanriBunrui === 'D04' ? 'D04:中規模病院' :
						r.kanriBunrui === 'D05' ? 'D05:大病院' :
						r.kanriBunrui === 'D06' ? 'D06:大規模病院' :
						r.kanriBunrui === 'D07' ? 'D07:その他' :
						r.kanriBunrui === 'P01' ? 'P01:病院 薬剤師' :
						r.kanriBunrui === 'P02' ? 'P02:診療所 薬剤師' :
						r.kanriBunrui === 'P03' ? 'P03:調剤薬局 薬剤師' :
						r.kanriBunrui === 'P04' ? 'P04:その他 薬剤師' : r.kanriBunrui
					),
					filterType: 'candidates'
				},
				'tierRank': {
					headerName: 'Tierランク',
					defaultShow: false,
					width: 80,
					hidden: session.isCareNet === false,
					value: (r) => r.mrpStats?.rank?.name,
					filterType: 'candidates'
				},
				'mailStatus': {
					headerName: 'メール受信可否',
					defaultShow: false,
					hidden: session.isCareNet === false,
					value: (r) => r.mailStatus,
					filterType: 'candidates'
				},
				'cnUserType': {
					headerName: 'CN会員',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.personalId ? '会員' : '非会員',
					filterType: 'candidates'
				},
				'actorStats': {
					headerName: 'CN出演歴',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.actor ? flatten([
						r.actor.isComActor ? 'COM' : null,
						r.actor.isTvActor ? 'TV' : null,
						r.actor.isDvdActor ? 'DVD' : null
					]) : [],
					renderCell: (params) => renderer.multiRow(params.value),
					filterType: 'candidates'
				},
				'picksExpert': {
					headerName: 'DrsPicks',
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.picksExpert ? 'Expert' : '-',
					filterType: 'candidates'
				},
				'topAccessTimes': {
					headerName: 'アクセス時間帯',
					defaultShow: false,
					width: 150,
					hidden: session.isCareNet === false,
					value: (r) => r.topAccessTimes?.map(time => {
						return (
							time.dayOfWeek === 'Mon' ? '月曜' :
							time.dayOfWeek === 'Tue' ? '火曜' :
							time.dayOfWeek === 'Wed' ? '水曜' :
							time.dayOfWeek === 'Thu' ? '木曜' :
							time.dayOfWeek === 'Fri' ? '金曜' :
							time.dayOfWeek === 'Sat' ? '土曜' :
							time.dayOfWeek === 'Sun' ? '日曜' : ''
						) + `\t${time.hourOfDay * 3}～${time.hourOfDay * 3 + 2}時`
					}),
					renderCell: (params) => renderer.multiRow(params.value),
					filterType: 'accessTime',
					split: 3
				},
				'academiaSinryoka': {
					headerName: 'AC 診療科',
					defaultShow: false,
					width: 100,
					hidden: session.isCareNet === false,
					value: (r) => r.academia?.sinryoka?.name,
					filterType: 'candidates'
				},
				'academiaDiseases': {
					headerName: 'AC 選択疾患',
					defaultShow: false,
					width: 150,
					hidden: session.isCareNet === false,
					value: (r) => r.academia?.diseases?.map(disease => disease.name),
					renderCell: (params) => renderer.multiRow(params.value, "horizon", 10),
					filterType: 'candidates'
				},
				'academiaLastPageView': {
					headerName: 'AC 前月PV',
					defaultShow: false,
					width: 100,
					hidden: session.isCareNet === false,
					type: 'number',
					align: 'right',
					value: (r) => r.academia?.pageView,
					filterType: 'histgram'
				},
				'academiaLastInterest': {
					headerName: 'AC 前月Interest',
					defaultShow: false,
					width: 100,
					hidden: session.isCareNet === false,
					type: 'number',
					align: 'right',
					value: (r) => r.academia?.interestCount,
					filterType: 'histgram'
				},
				'dpcScore': {
					headerName: 'DPCスコア',
					hidden: hideResult || hideDPC,
					defaultShow: false,
					value: (r) => r.score?.dpc?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { dpcRank?: number }) => {
						return (value && value !== Infinity && row.dpcRank && row.dpcRank !== Infinity) ? (
							`${numberFormat(value)}件 (${row.dpcRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'dpcRank',
					filterType: 'histgram',
					histgram: { unit: 100 }
				},
				'dpcRank': {
					headerName: 'DPC順位',
					hidden: true,
					value: (r) => r.score?.dpc?.rank,
					...columnBase.rank
				},
				'cancerScore': {
					headerName: 'がんスコア',
					hidden: hideResult || hideCancer,
					defaultShow: false,
					value: (r) => r.score?.cancer?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { cancerRank?: number }) => {
						return (value && value !== Infinity && row.cancerRank && row.cancerRank !== Infinity) ? (
							`${numberFormat(Math.floor(value * 100) / 100)}pt (${row.cancerRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'cancerRank',
					filterType: 'histgram'
				},
				'cancerRank': {
					headerName: 'がん順位',
					hidden: true,
					value: (r) => r.score?.cancer?.rank,
					...columnBase.rank
				},
				'trialScore': {
					headerName: '治験・研究スコア',
					hidden: hideResult || hideTrial,
					defaultShow: false,
					value: (r) => r.score?.trial?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { trialRank?: number }) => {
						return (value && value !== Infinity && row.trialRank && row.trialRank !== Infinity) ? (
							`${numberFormat(Math.floor(value * 100) / 100)}pt (${row.trialRank}位)`
						) : ''
					},
					suffix: '件',
					subField: 'trialRank',
					filterType: 'histgram'
				},
				'trialRank': {
					headerName: '治験・研究順位',
					hidden: true,
					value: (r) => r.score?.trial?.rank,
					...columnBase.rank
				},
				'trialRoles': {
					headerName: '治験での役割',
					hidden: hideResult || hideTrial,
					width: 120,
					defaultShow: false,
					value: (r) => r.score?.trial?.roles,
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: 'candidates'
				},
				'trialCount': {
					headerName: '治験参加数',
					hidden: hideResult || hideTrial,
					value: (r) => r.score?.trial?.trialCount,
					...columnBase.count
				},
				'guidelineScore': {
					headerName: 'ガイドラインスコア',
					hidden: hideResult || hideGuideLine,
					defaultShow: false,
					value: (r) => r.score?.guideline?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { guidelineRank?: number }) => {
						return (value && value !== Infinity && row.guidelineRank && row.guidelineRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.guidelineRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'guidelineRank',
					filterType: 'histgram'
				},
				'guidelineRank': {
					headerName: 'ガイドライン順位',
					hidden: true,
					value: (r) => r.score?.guideline?.rank,
					...columnBase.rank
				},
				'journalScore': {
					headerName: '論文スコア',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.score?.journal?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { journalRank?: number, journalScore: number }) => {
						return (value && value !== Infinity && row.journalRank && row.journalRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.journalRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'journalRank',
					filterType: 'histgram'
				},
				'journalRank': {
					headerName: '論文順位',
					hidden: true,
					value: (r) => r.score?.journal?.rank,
					...columnBase.rank
				},
				'journalERA': {
					headerName: '論文作成時期',
					hidden: hideResult || hideJournal,
					value: (r) => {
						if (r.score?.journal) {
							const scores = [
								{ label: 'F', score: r.score.journal.farTimeScore ?? 0 }, 
								{ label: 'M', score: r.score.journal.middleTimeScore ?? 0 },
								{ label: 'N', score: r.score.journal.nearTimeScore ?? 0 }
							].filter(s => s.score > 0)
							if (scores.length > 0) {
								scores.sort((a, b) => b.score - a.score)
								/*
								const set = scores.map((s, i, arr) => (
									i === 0 ? s.label : arr[i-1].score === s.score ? `=${s.label}` : `>${s.label}`
								)).join('')
								return set
								*/
								return JournalERAMap[scores.map(s => s.label).join('>')] ?? null
							}
						}
						return null
					},
					filterType: 'candidates'
				},
				'journalFarTimesCount': {
					headerName: '古論文件数',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.score?.journal?.farTimeCount,
					...columnBase.count
				},
				'journalMiddleTimesCount': {
					headerName: '中論文件数',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.score?.journal?.middleTimeCount,
					...columnBase.count
				},
				'journalNearTimesCount': {
					headerName: '新論文件数',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.score?.journal?.nearTimeCount,
					...columnBase.count
				},
				'journalFarTimesRate': {
					headerName: '古論文割合',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => {
						if (r.score?.journal && (r.score.journal.farTimeCount ?? 0) > 0) {
							return (r.score.journal.farTimeScore ?? 0) / (
								(r.score.journal.farTimeScore ?? 0) +
								(r.score.journal.middleTimeScore ?? 0) +
								(r.score.journal.nearTimeScore ?? 0)
							)
						}
						return null
					},
					...columnBase.percent
				},
				'journalMiddleTimesRate': {
					headerName: '中論文割合',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => {
						if (r.score?.journal && (r.score.journal.middleTimeScore ?? 0) > 0) {
							return (r.score.journal.middleTimeScore ?? 0) / (
								(r.score.journal.farTimeScore ?? 0) +
								(r.score.journal.middleTimeScore ?? 0) +
								(r.score.journal.nearTimeScore ?? 0)
							)
						}
						return null
					},
					...columnBase.percent
				},
				'journalNearTimesRate': {
					headerName: '新論文割合',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => {
						if (r.score?.journal && (r.score.journal.nearTimeScore ?? 0) > 0) {
							return (r.score.journal.nearTimeScore ?? 0) / (
								(r.score.journal.farTimeScore ?? 0) +
								(r.score.journal.middleTimeScore ?? 0) +
								(r.score.journal.nearTimeScore ?? 0)
							)
						}
						return null
					},
					...columnBase.percent
				},
				'single': {
					headerName: '単独論文',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.single,
					...columnBase.count
				},
				'first': {
					headerName: '筆頭著者',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.first,
					...columnBase.count
				},
				'second': {
					headerName: '第二著者',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.score?.journal?.counts.second,
					...columnBase.count
				},
				'last': {
					headerName: '責任著者',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.last,
					...columnBase.count
				},
				'lastSecond': {
					headerName: '第二責任著者',
					hidden: hideResult || hideJournal,
					defaultShow: false,
					value: (r) => r.score?.journal?.counts.lastSecond,
					...columnBase.count
				},
				'total': {
					headerName: '合計論文数',
					hidden: hideResult || hideJournal,
					value: (r) => r.score?.journal?.counts.total,
					...columnBase.count
				},
				'researchScore': {
					headerName: '研究スコア',
					hidden: hideResult || hideResearch,
					defaultShow: false,
					value: (r) => r.score?.research?.score,
					align: 'right',
					type: 'number',
					width: 120,
					valueFormatter: (value, row: { researchRank?: number }) => {
						return (value && value !== Infinity && row.researchRank && row.researchRank !== Infinity) ? (
							`${numberFormat(value)}pt (${row.researchRank}位)`
						) : ''
					},
					suffix: 'pt',
					subField: 'researchRank',
					filterType: 'histgram'
				},
				'researchRank': {
					headerName: '研究順位',
					hidden: true,
					value: (r) => r.score?.research?.rank,
					...columnBase.rank
				},
				'convention': {
					headerName: '学会',
					width: 300,
					hidden: hideResult || hideConvention,
					value: (r) => r.convention?.flatMap(
						cnv => cnv.score > 0 ? flatJoin('\t',
							cnv.numberOfTimes,
							cnv.conventionName?.replaceAll('　', ' '),
							cnv.role
						) : []
					),
					renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
					filterType: "text"
				},
				'kojinCode': {
					headerName: 'DCF個人コード',
					width: 80,
					defaultShow: false,
					value: (r) => r.kojinCode,
					filterType: 'text'
				},
				'personalId': {
					headerName: 'PID',
					width: 100,
					hidden: session.isCareNet === false,
					defaultShow: false,
					value: (r) => r.personalId,
					filterType: 'text'
				}
			}
			if (hideResult === false && hideGuideLine === false) {
				const DEF = '__default_'
				const guideLineTypes = distinct(search.guideLine?.flatMap(
					guideLine => guideLine.hasDoctor ? (guideLine.guideLineType ?? DEF) : []
				) ?? [])
				guideLineTypes.sort((a, b) => a === DEF ? -1 : b === DEF ? 1 : a.localeCompare(b))
				guideLineTypes.forEach((guideLineType) => {
					const field = guideLineType === DEF ? 'guideLineKojin' : `guideLineKojin-${guideLineType}`
					columnsDef[field] = {
						headerName: guideLineType === DEF ? 'ガイドライン' : guideLineType,
						width: 300,
						limitRow: 5,
						value: (r) => r.guideLine?.flatMap(
							guide => {
								const type = search.guideLine?.find(
									guideLine => guideLine.guideLineId === guide.guideLineId
								)?.guideLineType ?? null
								if (guideLineType === DEF) {
									if (type !== null) return []
								} else {
									if (type !== guideLineType) return []
								}
								if (guide.score === 0) return []
								return flatJoin('\t',
									guide.guideLineName?.replaceAll('　', ' '),
									guide.researcherClass,
									guide.postNames
								)
							}
						),
						renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
						filterType: "text"
					}
				})
			}
			if (hideResult === false && search.metaTag) {
				search.metaTag.forEach(metaTag => {
					columnsDef[`meta-${metaTag.key}`] = {
						headerName: metaTag.label,
						width: 200,
						value: (r) => r.metaTag?.flatMap(kojinsMetaTag => kojinsMetaTag.key === metaTag.key ? kojinsMetaTag.value : []),
						renderCell: (params) => renderer.multiRow(params.value, "vertical", 5),
						filterType: 'candidates'
					}
				})
			}
			if (evaluationForm) {
				if (evaluationForm.sections) {
					columnsDef['evaluation-total'] = {
						headerName: '総合評価',
						width: 80,
						align: 'right',
						value: ({ evaluation }) => evaluation?.totalScore,
						valueFormatter: (value) => valueFormatter({ value, unit: '点' }),
						suffix: '点',
						filterType: 'histgram'
					}
					columnsDef['evaluation-judgement'] = {
						headerName: '総合判定',
						width: 150,
						value: ({ evaluation }) => evaluation?.judgement,
						filterType: 'candidates'
					}
					evaluationForm.sections.forEach((section, i) => {
						columnsDef[`evaluation-section-${i}-total`] = {
							headerName: section.sectionName,
							width: 80,
							align: 'right',
							defaultShow: false,
							value: ({ evaluation }) => evaluation?.sections?.at(i)?.sectionTotal,
							valueFormatter: (value) => valueFormatter({ value, unit: '点' }),
							suffix: '点',
							filterType: 'histgram'
						}
						section.fields.forEach((field, j) => {
							columnsDef[`evaluation-section-${i}-${j}-content`] = {
								headerName: field.contentFieldId,
								width: 200,
								defaultShow: false,
								value: ({ evaluation }) => evaluation?.sections?.at(i)?.records.at(j)?.content,
								filterType: 'text'
							}
							columnsDef[`evaluation-section-${i}-${j}-score`] = {
								headerName: field.scoreFieldId,
								width: 60,
								defaultShow: false,
								align: 'center',
								value: ({ evaluation }) => evaluation?.sections?.at(i)?.records.at(j)?.score,
								filterType: 'candidates'
							}
						})
					})
					evaluationForm.considerations.forEach((consideration, i) => {
						columnsDef[`evaluation-consideration-${i}`] = {
							headerName: consideration,
							width: 200,
							defaultShow: false,
							value: ({ evaluation }) => evaluation?.considerations?.at(i)?.content,
							filterType: 'text'
						}
					})
				}
			}
			return columnsDef
		},
		[
			session, search, onClickLink, evaluationForm,
			hideResult, hideDPC, hideCancer, hideTrial, hideJournal, hideConvention, hideGuideLine, hideResearch
		]
	)
	return { columnsDef }
}